/* The Rate Reset: calculation engine.
   One file, no dependencies, classic script (window.RateReset) and Node module (tests).
   Method: research/04-product-method.md §6. Every number the owner sees comes from here. */
(function (root) {
  'use strict';

  // ---------------------------------------------------------------- sourced defaults
  // Each default is shown to the owner with its source and is editable. Targets are never defaulted silently.
  const SOURCES = {
    fica: 'Employer Social Security 6.2% + Medicare 1.45% (IRS)',
    futa: 'FUTA 0.6% on the first $7,000 of each employee\'s wages (IRS, after the normal state credit)',
    payrollExtras: 'BLS Employer Costs for Employee Compensation, June 2026: legally required benefits for service jobs = 11.4% of wages. Replace with yours: last year\'s employer payroll taxes + workers\' comp ÷ gross wages.',
    perMile: 'IRS standard business mileage rate, 76¢ a mile from July 1, 2026',
    card: '2.9% + 30¢, a common published US card rate (Jobber Payments, Stripe online). Use your processor\'s.',
    bank: '0.8% capped at $5, Stripe\'s published US bank-debit (ACH) rate. Use your processor\'s.'
  };

  const FREQUENCIES = {
    w1: { label: 'Every week', short: 'weekly', perYear: 52 },
    w2: { label: 'Every 2 weeks', short: 'every 2 weeks', perYear: 26 },
    m2: { label: 'Twice a month', short: 'twice a month', perYear: 24 },
    w3: { label: 'Every 3 weeks', short: 'every 3 weeks', perYear: 52 / 3 },
    w4: { label: 'Every 4 weeks', short: 'every 4 weeks', perYear: 13 },
    m1: { label: 'Once a month', short: 'monthly', perYear: 12 },
    custom: { label: 'Other', short: 'on your schedule', perYear: null }
  };

  const PAY_MODELS = {
    owner: 'I do the cleaning myself',
    hourly: 'Employees paid by the hour (W-2)',
    commission: 'Employees paid a share of each job (W-2)',
    c1099pct: 'Contractors paid a share of each job (1099)',
    c1099hourly: 'Contractors paid by the hour (1099)'
  };

  function defaultBusiness() {
    return {
      name: '', ownerName: '', phone: '', email: '', state: '',
      payModel: 'owner',
      wage: 20,                 // $/h: W-2 or contractor hourly; for 'owner', the wage you'd pay someone to replace you
      commissionPct: 0.45,      // share of the job price paid to the cleaner(s)
      payDrive: true,           // drive time between jobs is paid (W-2: hours worked under 29 CFR 785.38)
      otPremium: 0,             // average overtime premium, share of wages
      ptoAccrual: 0,            // paid hours not worked per hour worked (e.g. 1/30 for 1 h sick leave per 30 h)
      payrollExtras: 0.114,     // employer taxes + workers' comp, share of wages (W-2 models only)
      supplies: 5,              // $ per visit, used when a client has none of their own
      perMile: 0.76,
      mph: 30,                  // to estimate miles from drive minutes when a client's miles are blank
      cardPct: 0.029, cardFixed: 0.30,
      bankPct: 0.008, bankCap: 5,
      salesTax: 0,              // rate collected on top of the price (a pass-through)
      skipRate: 0,              // share of scheduled visits that don't happen (holidays, skips)
      overheadMonthly: 0,       // insurance, software, phone, marketing, accounting...
      overheadHoursMonthly: 0,  // work hours to spread it over; 0 = the hours in the client list
      target: { mode: 'hourly', hourly: null, margin: 0.15, perhour: null },
      maxStepPct: 0.20, maxStepUsd: 30, unit: 5,   // owners report losses clustering on +$20–60 jumps (research/01 §4.2)
      secondStepMonths: 6,
      effectiveDate: '',        // first visit at the new price; '' = next January 1
      noticeDays: 30,
      refill: { on: false, share: 0.5, months: 2 }
    };
  }

  function defaultClient(over) {
    return Object.assign({
      id: uid(), name: '', price: null, freq: 'w2', visitsPerYear: null,
      crew: 1, hoursOnSite: null, driveMin: 20, miles: null, supplies: null,
      pay: 'card', state: '', since: null, lastRaise: null,
      flags: { longtime: false, friend: false, fixedIncome: false, referral: false, difficult: false, latePay: false },
      keep: false, notes: '',
      track: { status: 'draft', sentOn: '', answeredOn: '', finalPrice: null, newFreq: '', reason: '' }
    }, over || {});
  }

  function uid() { return 'c' + Math.random().toString(36).slice(2, 10); }

  // ---------------------------------------------------------------- labor
  // Payroll-based rate t from its parts (method 6.2): wage bases make FUTA and SUTA small on full-year wages.
  function payrollFromParts(p) {
    const W = Math.max(+p.annualWages || 0, 1);
    const futa = 0.006 * Math.min(W, 7000) / W;
    const suta = (+p.sutaRate || 0) * Math.min(W, +p.sutaBase || 0) / W;
    const wc = (+p.wcPer100 || 0) / 100;
    return 0.0765 + futa + suta + wc + (+p.other || 0);
  }

  // Loaded cost of one paid working hour: c_L = w (1 + OT) (1 + PTO) (1 + t)
  function loadedHourly(biz, wage) {
    const w = wage == null ? +biz.wage : +wage;
    return w * (1 + +biz.otPremium) * (1 + +biz.ptoAccrual) * (1 + +biz.payrollExtras);
  }

  function visitsPerYear(c, biz) {
    if (c.freq === 'custom' || c.visitsPerYear) return Math.max(+c.visitsPerYear || 0, 0);
    const f = FREQUENCIES[c.freq] || FREQUENCIES.w2;
    return f.perYear * (1 - (+biz.skipRate || 0));
  }

  // ---------------------------------------------------------------- one client
  // Returns the visit economics at the client's current price (or at `price` when given).
  // Names follow the method: H labor-hours, C0 price-independent cost, r price-proportional rate,
  // CM contribution, OP operating profit after overhead (o per labor-hour).
  function economics(c, biz, o, price) {
    const P = price == null ? +c.price : +price;
    const n = Math.max(+c.crew || 1, 1);
    const hs = +c.hoursOnSite || 0;
    const ht = (+c.driveMin || 0) / 60;
    const Hs = n * hs, Ht = n * ht, H = Hs + Ht;
    const v = visitsPerYear(c, biz);
    const tax = +biz.salesTax || 0;
    const model = biz.payModel;

    const milesEstimated = c.miles == null || c.miles === '';
    const miles = milesEstimated ? ht * (+biz.mph || 0) : +c.miles;   // one vehicle per crew: miles count once
    const vehicle = miles * (+biz.perMile || 0);
    const supplies = c.supplies == null || c.supplies === '' ? +biz.supplies || 0 : +c.supplies;

    // payment fees: the card fee applies to the taxed total
    let feePct = 0, feeFixed = 0, bankCapped = false;
    if (c.pay === 'card') { feePct = (+biz.cardPct || 0) * (1 + tax); feeFixed = +biz.cardFixed || 0; }
    else if (c.pay === 'bank') {
      feePct = (+biz.bankPct || 0) * (1 + tax);
      const cap = +biz.bankCap || 0;
      if (cap > 0 && P * feePct > cap) { bankCapped = true; feePct = 0; feeFixed = cap; }
    }

    // labor by pay model
    const t = +biz.payrollExtras || 0;
    const paidHours = biz.payDrive === false ? Hs : H;
    let labor = 0, laborPct = 0, ownerLabor = 0;
    const cL = loadedHourly(biz);
    if (model === 'hourly') labor = paidHours * cL;
    else if (model === 'commission') laborPct = (+biz.commissionPct || 0) * (1 + t);
    else if (model === 'c1099pct') laborPct = +biz.commissionPct || 0;
    else if (model === 'c1099hourly') labor = paidHours * (+biz.wage || 0);
    else ownerLabor = H * cL;   // owner: not a cash cost; shown as its own line at replacement cost

    const C0 = labor + vehicle + supplies + feeFixed;
    const r = feePct + laborPct;
    const CM = P * (1 - r) - C0;
    const overhead = (o || 0) * H;
    const OP = CM - overhead;
    return {
      P, v, n, Hs, Ht, H, miles, milesEstimated, vehicle, supplies,
      fee: feePct * P + feeFixed, feePct, feeFixed, bankCapped,
      labor: labor + laborPct * P, laborFixed: labor, laborPct, ownerLabor, cL,
      C0, r, CM, m: P ? CM / P : 0, overhead, o: o || 0, OP, opm: P ? OP / P : 0,
      revPerHour: H ? P / H : 0, revPerOnsiteHour: Hs ? P / Hs : 0,
      cmPerHour: H ? CM / H : 0,
      earnPerHour: H ? OP / H : 0,            // owner model: what you keep per hour of your time here
      laborShare: P ? (labor + laborPct * P) / P : 0,
      perYear: { revenue: P * v, CM: CM * v, OP: OP * v, hours: H * v }
    };
  }

  // Target price for the owner's goal (method 6.4).
  function targetPrice(e, biz) {
    const T = biz.target || {};
    const mode = T.mode || 'hourly';
    let pStar = null;
    if (mode === 'hourly' && T.hourly != null && T.hourly !== '') {
      pStar = (e.C0 + (e.o + +T.hourly) * e.H) / (1 - e.r);
    } else if (mode === 'margin' && T.margin != null && T.margin !== '') {
      const d = 1 - e.r - +T.margin;
      pStar = d > 0 ? (e.C0 + e.o * e.H) / d : Infinity;
    } else if (mode === 'perhour' && T.perhour != null && T.perhour !== '') {
      pStar = +T.perhour * e.H;
    }
    return pStar;
  }

  const EPS = 1e-9;
  function ceilTo(x, u) { return Math.ceil(x / u - EPS) * u; }
  function floorTo(x, u) { return Math.floor(x / u + EPS) * u; }

  // The capped step from a price: never above the % or $ limit, rounded down to the unit.
  function capFrom(P, biz) {
    const u = +biz.unit || 1;
    const lim = Math.min(
      biz.maxStepPct == null || biz.maxStepPct === '' ? Infinity : P * (1 + +biz.maxStepPct),
      biz.maxStepUsd == null || biz.maxStepUsd === '' ? Infinity : P + +biz.maxStepUsd);
    if (!isFinite(lim)) return Infinity;
    let cap = floorTo(lim, u);
    if (cap <= P + EPS) cap = ceilTo(P + EPS, u) > lim ? P : ceilTo(P + EPS, u);   // a limit below one unit
    return cap;
  }

  // The plan for one client: steps from today's price to the rounded target (method 6.4, extended to steps).
  function plan(c, biz, o) {
    const e = economics(c, biz, o);
    const pStar = targetPrice(e, biz);
    const u = +biz.unit || 1;
    const out = { e, pStar, target: null, steps: [], tier: 'notarget', pNew: e.P, x: 0, eNew: e, years: yearsFlat(c, biz) };
    // a price and time on site are both needed: drive minutes alone are not a visit
    if (!(e.P > 0) || !(e.Hs > 0) || !(e.H > 0)) { out.tier = 'incomplete'; return out; }
    if (pStar == null) return out;
    if (!isFinite(pStar)) { out.tier = 'impossible'; return out; }
    const target = ceilTo(pStar, u);
    out.target = target;
    if (pStar <= e.P + EPS) { out.tier = 'meets'; return out; }
    if (c.keep) { out.tier = 'kept'; return out; }

    // up to 3 dated steps, each within the cap
    let price = e.P;
    const first = effectiveDate(biz);
    for (let i = 0; i < 3 && price < target - EPS; i++) {
      const next = Math.max(price, Math.min(target, capFrom(price, biz)));
      if (next <= price + EPS) break;
      out.steps.push({ price: next, from: addMonths(first, i * (+biz.secondStepMonths || 6)) });
      price = next;
    }
    out.pNew = out.steps.length ? out.steps[0].price : e.P;
    out.x = out.pNew / e.P - 1;
    out.eNew = economics(c, biz, o, out.pNew);
    out.final = price;
    out.eFinal = economics(c, biz, o, price);
    out.tier = out.eNew.CM <= 0 ? 'losing' : out.steps.length > 1 || price < target - EPS ? 'steps' : 'raise';
    return out;
  }

  function yearsFlat(c, biz) {
    const ref = new Date(effectiveDate(biz) + 'T00:00:00Z').getUTCFullYear();
    const y = +c.lastRaise || +c.since;
    return y ? Math.max(ref - y, 0) : null;
  }

  // ---------------------------------------------------------------- break-even (method 6.5)
  // Refill value per lost visit: the freed hours refilled at the client's target price.
  function refillPerVisit(p, biz, kNew) {
    const R = biz.refill || {};
    if (!R.on || !p.e.v) return 0;
    const k = kNew != null ? kNew : (p.target ? economics({ ...p.client }, biz, p.e.o, p.target).cmPerHour : 0);
    const months = Math.min(Math.max(+R.months || 0, 0), 12);
    return Math.max((+R.share || 0) * p.e.H * p.e.v * k * (12 - months) / 12 - (+R.cost || 0), 0) / p.e.v;
  }

  // Chance the client must accept for this step to pay: p* = (CM − R) / (CM' − R).
  function acceptNeeded(CM, CMn, R) {
    R = R || 0;
    if (CM <= 0) return { kind: 'gain' };        // losing this client loses no contribution
    if (R >= CM) return { kind: 'free' };        // refill earns at least as much as keeping them
    if (CMn <= CM) return { kind: 'none' };
    return { kind: 'share', p: (CM - R) / (CMn - R), loss: (CMn - CM) / (CMn - R) };
  }

  // Textbook check: ℓ* = x(1 − r) / (m + x(1 − r)), m on avoidable costs.
  function breakEvenLoss(x, r, m) { return x * (1 - r) / (m + x * (1 - r)); }

  // ---------------------------------------------------------------- the book
  function overheadPerHour(clients, biz) {
    if (biz.overheadPerHour != null) return +biz.overheadPerHour;   // tests, and owners who know it
    const F = (+biz.overheadMonthly || 0) * 12;
    if (!F) return 0;
    let hours = (+biz.overheadHoursMonthly || 0) * 12;
    if (!hours) for (const c of clients) { const e = economics(c, biz, 0); if (e.P > 0 && e.Hs > 0) hours += e.H * e.v; }
    return hours ? F / hours : 0;
  }

  function book(clients, biz, opts) {
    opts = opts || {};
    const o = overheadPerHour(clients, biz);
    const rows = clients.map(c => {
      const p = plan(c, biz, o);
      p.client = c;
      p.R = refillPerVisit(p, biz, opts.kNew);
      p.accept = p.steps.length ? acceptNeeded(p.e.CM, p.eNew.CM, p.R) : null;
      return p;
    });
    const ok = rows.filter(p => p.tier !== 'incomplete');
    const sum = (list, f) => list.reduce((s, p) => s + f(p), 0);
    const today = {
      revenue: sum(ok, p => p.e.perYear.revenue), CM: sum(ok, p => p.e.perYear.CM),
      OP: sum(ok, p => p.e.perYear.OP), hours: sum(ok, p => p.e.perYear.hours)
    };
    const after = {
      revenue: sum(ok, p => p.eNew.perYear.revenue), CM: sum(ok, p => p.eNew.perYear.CM),
      OP: sum(ok, p => p.eNew.perYear.OP), hours: today.hours
    };
    const raised = ok.filter(p => p.steps.length).sort(byMostRaised);
    return {
      o, rows, today, after, raised,
      canLose: canLose(raised, today, after),
      groupBreakEven: groupBreakEven(raised),
      ranking: ok.slice().sort((a, b) => a.e.cmPerHour - b.e.cmPerHour)
    };
  }

  // The likeliest leavers are the most-raised clients: biggest % step first, then biggest $.
  function byMostRaised(a, b) { return (b.x - a.x) || ((b.pNew - b.e.P) - (a.pNew - a.e.P)); }

  // How many raised clients, most-raised first, could leave before the year is worse than today.
  function canLose(raised, today, after) {
    let total = after.CM, n = 0;
    for (const p of raised) {
      const next = total - p.eNew.perYear.CM + p.R * p.e.v;
      if (next < today.CM - 0.005) break;
      total = next; n++;
    }
    return { n, of: raised.length, total };
  }

  // Uniform-loss break-even for the positive-contribution clients being raised (method 6.5).
  function groupBreakEven(raised) {
    const pos = raised.filter(p => p.e.CM > 0);
    const cm = pos.reduce((s, p) => s + p.e.perYear.CM, 0);
    const cmn = pos.reduce((s, p) => s + p.eNew.perYear.CM, 0);
    return cmn > 0 ? 1 - cm / cmn : null;
  }

  // Annual contribution if the named clients leave, with or without refill.
  function scenario(b, lostIds, refill) {
    let total = b.after.CM;
    for (const p of b.rows) {
      if (!lostIds.includes(p.client.id) || p.tier === 'incomplete') continue;
      total -= p.eNew.perYear.CM;
      if (refill) total += p.R * p.e.v;
    }
    return { CM: total, vsToday: total - b.today.CM };
  }

  // ---------------------------------------------------------------- dates (UTC, ISO yyyy-mm-dd)
  function iso(d) { return d.toISOString().slice(0, 10); }
  function parse(s) { return new Date(s + 'T00:00:00Z'); }
  function addDays(s, n) { const d = parse(s); d.setUTCDate(d.getUTCDate() + n); return iso(d); }
  function addMonths(s, n) {
    const d = parse(s), day = d.getUTCDate();
    d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() + n);
    const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
    d.setUTCDate(Math.min(day, last));
    return iso(d);
  }
  function todayIso(now) { const d = now || new Date(); return iso(new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))); }
  function effectiveDate(biz, now) {
    if (biz.effectiveDate) return biz.effectiveDate;
    const t = parse(todayIso(now));
    return (t.getUTCFullYear() + 1) + '-01-01';
  }
  function daysBetween(a, b) { return Math.round((parse(b) - parse(a)) / 864e5); }

  // US federal holidays (observed), for counting business days.
  function holidays(year) {
    const nth = (m, wd, k) => { const d = new Date(Date.UTC(year, m, 1)); const off = (wd - d.getUTCDay() + 7) % 7; return iso(new Date(Date.UTC(year, m, 1 + off + 7 * (k - 1)))); };
    const last = (m, wd) => { const d = new Date(Date.UTC(year, m + 1, 0)); const off = (d.getUTCDay() - wd + 7) % 7; return iso(new Date(Date.UTC(year, m + 1, -off))); };
    const fixed = (m, day) => { const d = new Date(Date.UTC(year, m, day)); const wd = d.getUTCDay(); if (wd === 6) d.setUTCDate(day - 1); if (wd === 0) d.setUTCDate(day + 1); return iso(d); };
    return new Set([fixed(0, 1), nth(0, 1, 3), nth(1, 1, 3), last(4, 1), fixed(5, 19), fixed(6, 4), nth(8, 1, 1),
      nth(9, 1, 2), fixed(10, 11), nth(10, 4, 4), fixed(11, 25), fixed(11 + 1, 1)]);
  }
  function isBusinessDay(s) {
    const d = parse(s), wd = d.getUTCDay();
    if (wd === 0 || wd === 6) return false;
    const y = d.getUTCFullYear();
    return !holidays(y).has(s) && !holidays(y + 1).has(s);
  }
  // Business days strictly between two dates (the conservative reading, method 5.1 #2).
  function businessDaysBetween(a, b) {
    let n = 0;
    for (let s = addDays(a, 1); s < b; s = addDays(s, 1)) if (isBusinessDay(s)) n++;
    return n;
  }

  // When to send a notice for a change on `effective` (method 5.1): 10–30 days before, and at least
  // 5 business days between. Flags by state and payment; facts, not legal advice.
  function sendWindow(effective, c, biz) {
    const state = String(c.state || biz.state || '').toUpperCase();
    const earliest = addDays(effective, -30);
    let latest = addDays(effective, -10);
    while (businessDaysBetween(latest, effective) < 5) latest = addDays(latest, -1);
    const flags = [];
    if (state === 'CA') flags.push({ id: 'ca', text: 'California: send it 7 to 30 days before the change, in writing the client can keep, with how to cancel.' });
    if (state === 'NY') flags.push({ id: 'ny', text: 'New York: send it at least 5 business days and no more than 30 days before, by the way the client usually hears from you. Before charging the new price, get their OK, or let them cancel within 14 days of the first new charge with a pro-rated refund.' });
    if (state === 'MN') flags.push({ id: 'mn', text: 'Minnesota: a change without a clear written notice that says how to cancel may not be enforceable.' });
    if (c.pay === 'bank') flags.push({ id: 'autopay', text: 'Bank autopay: tell them the new amount and the date at least 10 days before the first new charge.' });
    if (c.pay === 'card') flags.push({ id: 'card', text: 'Card on file: send the notice the way your card-on-file agreement says you will, and keep a copy.' });
    const noticeDays = +biz.noticeDays || 30;
    let planned = addDays(effective, -noticeDays);
    const early = planned < earliest;
    if (early) flags.push({ id: 'early', text: 'A notice ' + noticeDays + ' days ahead is a courtesy. Where a state caps it at 30 days (CA, NY), send a short reminder inside the window too.' });
    return { earliest, latest, planned, early, flags };
  }

  // ---------------------------------------------------------------- formatting
  function money(n, cents) {
    if (n == null || !isFinite(n)) return '–';
    const neg = n < 0; n = Math.abs(n);
    const showCents = cents === true || (cents !== false && Math.round(n * 100) % 100 !== 0 && n < 1000);
    const s = n.toLocaleString('en-US', { minimumFractionDigits: showCents ? 2 : 0, maximumFractionDigits: showCents ? 2 : 0 });
    return (neg ? '−$' : '$') + s;
  }
  function pct(x, d) { return x == null || !isFinite(x) ? '–' : (x * 100).toFixed(d == null ? 0 : d).replace(/\.0+$/, '') + '%'; }
  function longDate(s) {
    if (!s) return '';
    return parse(s).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', day: 'numeric', year: 'numeric' });
  }
  function shortDate(s) {
    if (!s) return '';
    return parse(s).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
  }

  const api = {
    SOURCES, FREQUENCIES, PAY_MODELS,
    defaultBusiness, defaultClient, uid,
    payrollFromParts, loadedHourly, visitsPerYear, economics, targetPrice, plan, capFrom, ceilTo, floorTo,
    refillPerVisit, acceptNeeded, breakEvenLoss, overheadPerHour, book, scenario, canLose, byMostRaised,
    effectiveDate, addDays, addMonths, daysBetween, todayIso, holidays, isBusinessDay, businessDaysBetween, sendWindow,
    money, pct, longDate, shortDate
  };
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.RateReset = api;
})(typeof window !== 'undefined' ? window : globalThis);
